import React from "react";
import { ContentCard } from "../components/ContentCard";
import { PropertyInfoContent } from "./property/PropertyInfoContent";
import { PredictionOverviewPrice } from "../components/predictionOverview/PredictionOverviewPrice";
import { PropertyShared } from "../types/api.types";
import { BasicAppraiser } from "./propertiesCRUD/BasicAppraiser";
import { PropertyStatuses } from "./property/PropertyStatuses";
import { PropertyCommentContent } from "./property/PropertyCommentContent";
import { AppraiserRole, PropertyPurpose } from "../types/appraiser.types";
import { PredictionCompositeAssets } from "../components/PredictionCompositeAssets";
import { ComparablesSummaryTable } from "../components/comparablesSummary/ComparablesSummaryTable";
import { getPredictionsWithRealPrices } from "../helpers/pricePredictions.helpers";
import { DescriptionsTable } from "../components/comparablesSummary/DescriptionsTable";
import { ValuationSummary } from "../components/comparablesSummary/ValuationSummary";
import { PropertyFileList } from "./property/PropertyFileList";
import { PropertyDistances } from "./property/distances/PropertyDistances";
import { PropertyHazardousObjects } from "./property/hazardousObjects/PropertyHazardousObjects";
import { PropertyEditButton } from "./property/PropertyEditButton";
import { CenteredContentWrapper } from "../components/wrappers/CenteredContentWrapper";
import { PropertyLeadContact } from "../components/properties/PropertyLeadContact";
import { PropertyMarketInsights } from "./property/marketInsights/PropertyMarketInsights";
import { usePrintableQuery, useRouteQuery } from "../hooks/router.hooks";
import { ComparableSummaryTableChunked } from "../components/comparablesSummary/ComparableSummaryTableChunked";
import { PropertyGallery } from "./property/PropertyGallery";
import { PropertyLocationContent } from "./property/PropertyLocationContent";
import { AdjustmentsMap } from "../components/map/AdjustmentsMap";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FullReportBanner } from "./property/FullReportBanner";
import {
  LegalParagraphs,
  Paragraphs,
} from "../components/content/LegalParagraphs";
import { PropertySustainabilityReview } from "./property/PropertySustainabilityReview";
import { PropertyPenCertificate } from "./property/PropertyPenCertificate";
import { InfoRow } from "./propertiesCRUD/InfoRow";
import { getLithuanianDateString } from "common/helpers/dateStringHelpers";
import { getPropertyValueString } from "../helpers/property.helpers";
import { SimilarPropertiesInsights } from "./property/timeOnMarket/SimilarPropertiesInsights";
import { TransparencySimilarity } from "./property/TransparencySimilarity";
import { TransparencyAdjustmentsCorrection } from "./property/TransparencyAdjustmentsCorrection";
import { PredictionsConfidenceMeasure } from "./property/PredictionsConfidenceMeasure";
import { ConfidenceTooltip } from "../components/predictionOverview/ConfidenceTooltip";
import colors from "common/styles/colors";
import { PropertyQuickValuationSummary } from "./property/PropertyQuickValuationSummary";

interface PropertyShareProps {
  data: PropertyShared;
  isQuickValuation?: boolean;
}

export const PropertyShare: React.FC<PropertyShareProps> = ({
  data,
  isQuickValuation,
}) => {
  const { t } = useTranslation();
  const printable = usePrintableQuery();

  const hasAppraiser =
    !!data.basicOrganization &&
    data.property.appraiser.role !== AppraiserRole.ORGANIZATION_LEAD &&
    data.property.appraiser.role !== AppraiserRole.DATA_ENTRY;

  return (
    <div>
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          padding: "20px",
          marginBottom: "30px",
        }}
      >
        <InfoRow
          label={t("propertyDetails.valuationId")}
          value={data.property._id}
        />
        {!!data.property.reportNumber && (
          <InfoRow
            label="Ataskaitos numeris"
            value={data.property.reportNumber.toString()}
          />
        )}
        <InfoRow
          label="Sukūrimo data"
          value={getLithuanianDateString(data.property.createdAt)}
        />
        {data.confirmation && (
          <InfoRow
            label="Patvirtinimo data"
            value={getLithuanianDateString(data.confirmation.createdAt)}
          />
        )}
      </div>
      {!printable && !!data.property.propertyStatuses && (
        <PropertyStatuses
          latestStatus={data.property.propertyRequestStatus}
          propertyStatuses={data.property.propertyStatuses}
        />
      )}

      {!!data.property.leadContact && (
        <PropertyLeadContact
          leadContact={data.property.leadContact}
          createdAt={data.property.createdAt.toString()}
          updatedAt={data.property.updatedAtWithToken}
          discountCode={data.property.order?.discountCode}
          expectedValue={data.property.expectedValue}
          addons={data.purchasedAddons}
          compositeAssets={data.purchasedCompositeAssets}
          hidePdf={printable}
          deliveryTimeLabel={
            data.property.order?.deliveryTimeLabel ?? undefined
          }
          depositPaid={data.totalDepositPaid}
        />
      )}

      {hasAppraiser &&
        data.property.purpose === PropertyPurpose.Consultative && (
          <div style={{ marginBottom: "20px" }}>
            <FullReportBanner phone={data.property.appraiser.phoneNumber} />
          </div>
        )}

      {!data.hideProperty && (
        <ContentCard title="Informacija apie turtą">
          <PropertyInfoContent property={data.property} printable={printable} />
        </ContentCard>
      )}

      {!printable && !isQuickValuation && (
        <CenteredContentWrapper>
          {data.canEdit && (
            <PropertyEditButton
              propertyId={data.property._id}
              disabled={!data.canEdit}
              to={`/lead/details?createdPropertyId=${data.property._id}&simpleType=${data.property.simpleType}&editToken=${data.propertyToken}`}
            />
          )}
        </CenteredContentWrapper>
      )}

      {!!data.predictedPrice && (
        <ContentCard
          title={getPropertyValueString(data.property)}
          icon={
            data.pricePrediction?.confidence && (
              <Box display="flex" alignItems="center" gap={1}>
                <span>Modelio atliktų skaičiavimų patikimumo indeksas</span>
                <ConfidenceTooltip
                  confidence={data.pricePrediction.confidence}
                />
              </Box>
            )
          }
        >
          {!!data.predictedPrice && (
            <PredictionOverviewPrice
              predictedPrice={data.predictedPrice}
              property={data.property}
            />
          )}
          {!!data.compositeAssets && (
            <PredictionCompositeAssets
              propertyId={data.property._id}
              compositeAssets={data.compositeAssets}
              conservativeValue={data.conservativeValue}
            />
          )}
          {!!data.confidenceJustification?.final_price_justification && (
            <p
              style={{
                whiteSpace: "pre-wrap",
                color: colors.grey,
                marginTop: "40px",
              }}
            >
              {data.confidenceJustification.final_price_justification}
            </p>
          )}
        </ContentCard>
      )}

      {!!data.legal && (
        <ContentCard
          title={`Apie ${
            data.property.purpose === PropertyPurpose.Consultative
              ? "galimos kainos nustatymą"
              : "ataskaitą"
          }`}
        >
          <LegalParagraphs legals={data.legal} />
        </ContentCard>
      )}
      {hasAppraiser && (
        <div id="appraiser-info">
          <BasicAppraiser
            appraiser={data.property.appraiser}
            organization={data.basicOrganization}
            marginTop={0}
            confirmation={data.confirmation}
          >
            {!!data.appraiserLegal && (
              <Paragraphs paragraphs={data.appraiserLegal.paragraphs} />
            )}
          </BasicAppraiser>
        </div>
      )}
      {!!data.propertyComment && (
        <ContentCard title="Vertintojo komentaras">
          <PropertyCommentContent data={data.propertyComment} />
        </ContentCard>
      )}

      {!!data.legalMarketValue && (
        <ContentCard title={"Apie rinkos vertę"}>
          <LegalParagraphs legals={data.legalMarketValue} />
        </ContentCard>
      )}

      {!!data.methodology && (
        <ContentCard title="Skaičiavimų metodologija">
          <LegalParagraphs legals={data.methodology} />
        </ContentCard>
      )}

      {!!data.pricePrediction?.comparables.length && (
        <ContentCard title="Skaičiavimai">
          <Box
            id="property-adjustments-map"
            sx={{
              height: { xs: "300px", md: "450px" },
              mb: "30px",
            }}
          >
            <AdjustmentsMap
              property={data.property}
              comparables={data.pricePrediction.comparables}
              mapOptions={{
                mapTypeControl: false,
                streetViewControl: false,
                scrollwheel: undefined,
              }}
            />
          </Box>
          <ComparableSummaryTableChunked
            finalPricePredictions={data.pricePrediction}
            propertyId={data.property._id}
            propertyFromProps={data.property}
            chunkSize={printable ? 3 : null}
          />
        </ContentCard>
      )}
      {!!data.transparencyData && (
        <ContentCard
          title={data.confidenceJustification?.title ?? "Pasitikėjimo rodiklis"}
          caption={
            data.confidenceJustification?.description_paragraphs?.join(
              "\n\n"
            ) ??
            "Rodoma detali informacija, nes pasitikėjimo rodiklis yra mažesnis nei 75%"
          }
        >
          {!!data.transparencyData.confidence && (
            <div style={{ marginBottom: "30px" }}>
              <PredictionsConfidenceMeasure
                confidence={data.transparencyData.confidence}
              />
            </div>
          )}

          <TransparencySimilarity
            comparablesForReview={data.transparencyData.comparablesForReview}
            appliedFilters={data.transparencyData.applied_filters}
            property={data.property}
            readonly={printable}
            title="Vertintojo atliktos korekcijos palyginamųjų sandorių atrankoje"
          />
          <TransparencyAdjustmentsCorrection
            data={data.transparencyData}
            readonly={printable}
            title="Vertintojo atliktos korekcijos pritaikytose pataisose"
          />
        </ContentCard>
      )}
      {!!data.pricePrediction?.appraiserComment?.length && (
        <ContentCard title="Vertintojo komentaras dėl pataisų">
          <p style={{ whiteSpace: "pre-wrap" }}>
            {data.pricePrediction.appraiserComment}
          </p>
        </ContentCard>
      )}

      {printable && !data.hideGallery && (
        <PropertyGallery property={data.property} />
      )}
      {!!data.documents?.length && (
        <ContentCard title="Dokumentai">
          <PropertyFileList files={data.documents} />
        </ContentCard>
      )}
      {!!data.distances && (
        <PropertyDistances
          property={data.property}
          propertyToken={data.propertyToken || ""}
        />
      )}
      {!!data.hazardousObjects && (
        <PropertyHazardousObjects
          property={data.property}
          hazardousObjects={data.hazardousObjects}
          propertyToken={data.propertyToken || ""}
        />
      )}
      {!!data.property && !data.hidePenCertificate && (
        <PropertyPenCertificate property={data.property} />
      )}
      {!!data.locationDetails?.length && (
        <>
          <PropertySustainabilityReview
            property={data.property}
            locations={data.locationDetails}
          />
          <PropertyLocationContent locations={data.locationDetails} />
        </>
      )}
      {!!data.marketInsights && (
        <PropertyMarketInsights marketInsights={data.marketInsights} />
      )}
      {!!data.similarPropertiesInsights?.listings?.length && (
        <div id="similar-properties-insights">
          <SimilarPropertiesInsights
            property={data.property}
            similarListings={data.similarPropertiesInsights}
          />
        </div>
      )}
      {!data.predictedPrice && isQuickValuation && (
        <PropertyQuickValuationSummary property={data.property} isSticky />
      )}
    </div>
  );
};
